import type { CSSProperties } from 'react';
import { Scissors, Trash2 } from 'lucide-react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import {
  LEAF_CLUSTER_CELL_WIDTH,
  LEAF_CLUSTER_COLUMNS,
  LEAF_CLUSTER_GAP,
  LEAF_CLUSTER_PADDING
} from './tree-flow-layout.ts';

export type TreeFlowLeafClusterRow = {
  id: string;
  label: string;
};

export type TreeFlowLeafClusterNodeData = {
  rows: TreeFlowLeafClusterRow[];
  canActOnRow: boolean;
  actingRowId: string | null;
  /** Coin haut-gauche au survol : supprime le lien entrant et la carte du cluster. */
  onTrashRow: (rowId: string) => void;
  trashRowAriaLabel: string;
  /** Coin haut-droit au survol : supprime le lien entrant, garde la carte visible (orpheline) hors du cluster. */
  onCutRow: (rowId: string) => void;
  cutRowAriaLabel: string;
  /** Poignée source par carte, pour tirer un lien vers un nouveau subordonné directement depuis elle. */
  rowSourceConnectable: boolean;
  showTargetHandle: boolean;
  /** Défaut Top (arbre vertical) — passer Left pour un arbre horizontal. */
  targetPosition?: Position;
  /** Défaut Right — côté de la poignée source de chaque carte. */
  rowSourcePosition?: Position;
};

export type TreeFlowLeafClusterNodeType = Node<TreeFlowLeafClusterNodeData, string>;

/** Style de la poignée source de chaque carte, collée au bord indiqué par `position`. */
function rowHandleStyle(position: Position): CSSProperties {
  switch (position) {
    case Position.Bottom:
      return { position: 'absolute', bottom: -7, left: '50%', transform: 'translateX(-50%)' };
    case Position.Top:
      return { position: 'absolute', top: -7, left: '50%', transform: 'translateX(-50%)' };
    case Position.Left:
      return { position: 'absolute', left: -7, top: '50%', transform: 'translateY(-50%)' };
    default:
      return { position: 'absolute', right: -7, top: '50%', transform: 'translateY(-50%)' };
  }
}

/**
 * Cluster regroupant plusieurs feuilles (personnes sans descendance) d'un
 * même parent en une seule carte, pour éviter qu'un parent avec beaucoup de
 * rapports directs n'éclate en autant de nœuds/arêtes convergentes. Noms en
 * grille (colonnes fixes, cf. `tree-flow-layout.ts`), chacun dans sa propre
 * mini-carte.
 *
 * Chaque carte a sa propre poignée source (donner un premier subordonné
 * sans sortir la carte du cluster au préalable — dès que le lien existe,
 * elle en sort automatiquement au prochain rechargement), et les 2 mêmes
 * actions qu'un nœud standalone (corbeille/ciseaux, cf. `tree-flow-node.tsx`)
 * — jamais de choix cascade ici, une feuille n'a par définition aucun
 * sous-arbre. Jamais de target handle par carte : le cluster reçoit son
 * unique lien entrant via sa propre poignée (portée par l'appelant).
 */
export function TreeFlowLeafClusterNode({ data }: NodeProps<TreeFlowLeafClusterNodeType>) {
  const rowSourcePosition = data.rowSourcePosition ?? Position.Right;

  return (
    <div
      className="rounded-2xl bg-transparent"
      style={{
        padding: LEAF_CLUSTER_PADDING,
        borderStyle: 'dashed',
        borderWidth: 2.5,
        borderColor: 'hsl(var(--foreground) / 0.3)'
      }}
    >
      {data.showTargetHandle && (
        <Handle
          id="target"
          type="target"
          position={data.targetPosition ?? Position.Top}
          isConnectable={false}
          className="!size-3 !border-border !bg-muted"
        />
      )}
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${LEAF_CLUSTER_COLUMNS}, ${LEAF_CLUSTER_CELL_WIDTH}px)`,
          gap: LEAF_CLUSTER_GAP
        }}
      >
        {data.rows.map((row) => (
          <div
            key={row.id}
            className="group/row relative rounded-lg border-2 border-border bg-background px-2 py-1.5 text-center text-xs font-medium shadow-md"
          >
            <p className="truncate">{row.label}</p>

            {data.canActOnRow && (
              <>
                <button
                  type="button"
                  onClick={() => data.onTrashRow(row.id)}
                  title={data.trashRowAriaLabel}
                  disabled={data.actingRowId === row.id}
                  className="absolute -left-2.5 -top-2.5 flex items-center justify-center rounded-full border border-border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity nodrag nopan size-6 hover:text-destructive group-hover/row:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="size-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => data.onCutRow(row.id)}
                  title={data.cutRowAriaLabel}
                  disabled={data.actingRowId === row.id}
                  className="absolute -right-2.5 -top-2.5 flex items-center justify-center rounded-full border border-border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity nodrag nopan size-6 hover:text-foreground group-hover/row:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Scissors className="size-3.5" />
                </button>
              </>
            )}

            <Handle
              id={row.id}
              type="source"
              position={rowSourcePosition}
              isConnectable={data.rowSourceConnectable}
              style={rowHandleStyle(rowSourcePosition)}
              className="!size-3 !border-border !bg-muted"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
